import { defineStore } from 'pinia'
import NodeType from '../models/NodeType'
import dayjs from 'dayjs'
import { useQuestMaster } from './useQuestMaster'
import { useMissionStore } from './useMissionStore'
import { useUserStore } from './useUserStore'
import { useLandStore } from './useLandStore'

interface State {
  nodeTypes: Array<any>
  currentDeckNo: any
  quest: any
}

export const useNodeStore = defineStore('nodeStore', {
  state: (): State => ({
    nodeTypes: [],
    currentDeckNo: 0,
    quest: null,
  }),
  getters: {
    getQuest: state => {
      return state.quest
    },
    getNodeType: state => {
      return (id: Number) =>
        state.nodeTypes.find(x => {
          return x.nodeLvsList.find((lv: any) => lv.nodeId === id)
        })
    },
    getNodeTypes: state => {
      return (type: string) => {
        const nodeTypes = [
          ...state.nodeTypes
            .filter((n: any) => (type === 'normal' ? !n.landType : !!n.landType))
            .filter((n: any) => dayjs().isBefore(dayjs(n.until * 1000))),
        ]
        return nodeTypes.sort((p, c) => {
          if (p.isEvent) return -1
          if (c.isEvent) return 1
          return 0
        })
      }
    },
    getRelatedNodeTypes(state) {
      const arrayOfArray = (useLandStore().relatedLandTypes || []).map(landType => {
        return [...state.nodeTypes.filter(n => n.landType === landType)] || []
      })

      if (!arrayOfArray || process.server) {
        return []
      }

      const me = useUserStore().me

      const ownLandSelector = useLandStore().myOwnLandSectors || []
      const ownLandSelectorType = this.$nuxt.$util.flat(ownLandSelector).map(n => n.landSectorType)

      const newArray = arrayOfArray.map(n => {
        return n.filter(
          d =>
            (d.nodeClass[0] != 6 && me.landType == d.landType) ||
            !!ownLandSelectorType.find(l => d.nodeClass[0] == 6 && String(l).indexOf(String(d.landType)) == 0)
        )
      })

      return this.$nuxt.$util.flat(newArray).map(n => n.nodeType)
    },
    isInNodeEventTerm: state => {
      return state.nodeTypes
        .filter(n => n.isEvent)
        .some(n => {
          const since = dayjs(n.since * 1000)
          const until = dayjs(n.until * 1000)
          return (dayjs() as any).isBetween(since, until)
        })
    },
    getCurrentDeckNo: state => {
      return state.currentDeckNo || 0
    },
  },
  actions: {
    async fetchNodes() {
      let nodeTypes = await this.$nuxt.$questsService.getNodes()
      nodeTypes = nodeTypes
        .map(nodeType => {
          return Object.assign({}, nodeType, useQuestMaster().getMaster(nodeType.nodeType))
        })
        .map(n => new NodeType(n))
      nodeTypes.sort((p, c) => {
        if (p.nodeType < c.nodeType) return -1
        if (p.nodeType > c.nodeType) return 1
        return 0
      })
      this.nodeTypes = nodeTypes
    },
    async startQuest({ nodeId, deckNo }) {
      const quest = await this.$nuxt.$questsService.start(nodeId, deckNo)
      this.quest = Object.assign({}, this.quest, quest)
      this.currentDeckNo = deckNo
    },
    async goToNextQuest() {
      const quest = await this.$nuxt.$questsService.next()
      this.quest = Object.assign({}, this.quest, quest)
    },
    async continue() {
      const quest = await this.$nuxt.$questsService.continue()
      this.quest = Object.assign({}, this.quest, quest)
    },
    async checkUnclosedQuest() {
      const quest = await this.$nuxt.$questsService.getCurrent()
      if ((quest.status === 4 && !quest.consecutiveQuestId) || quest.status === 5) {
        this.quest = null
        return
      }
      if (quest.status === 4 && !!quest.consecutiveQuestId) {
        await this.$nuxt.$questsService.consecutiveClose()
        this.quest = null
        return
      }
      if (quest.status === 6 && !!quest.missionId) {
        const mission = await this.$nuxt.$questsService.getMissionInfo(quest.missionId)
        useMissionStore().setFrontierMission(mission)
      } else if (!quest.consecutiveQuestId) {
        this.quest = null
      }
      this.quest = Object.assign({}, this.quest, quest)
    },
    async quitQuest() {
      await this.$nuxt.$questsService.close()
      this.quest = null
    },
    async cancelQuest() {
      await this.$nuxt.$questsService.cancel()
      this.quest = null
    },
    async clearQuest() {
      this.quest = null
    },
  },
})
